import React, { createContext, useContext, useEffect, useRef, useState } from "react";

// --- Context Setup ---
const TaskStatusContext = createContext();

export const TaskStatusProvider = ({ children }) => {
  const [taskStatuses, setTaskStatuses] = useState({});
  const [taskData, setTaskData] = useState({});
  const [taskConnections, setTaskConnections] = useState({});
  const wsRefs = useRef({}); // TaskID -> WebSocket instances

  // --- Create WebSocket and track connection status ---
  const createWebSocketWithStatus = (url, taskId, onMessage) => {
    const ws = new WebSocket(url);

    ws.onopen = () => {
      console.log(`WebSocket for ${taskId} connected`);
      setTaskConnections((prev) => ({ ...prev, [taskId]: true }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        onMessage(data);
      } catch (e) {
        console.warn("Invalid WS data", e);
      }
    };

    ws.onerror = (err) => {
      console.error(`WebSocket error on ${taskId}:`, err);
    };

    ws.onclose = () => {
      console.log(`WebSocket for ${taskId} closed`);
      setTaskConnections((prev) => ({ ...prev, [taskId]: false }));
    };

    return ws;
  };

  // --- Register a new task and listen via WebSocket ---
  const registerTask = (taskId, wsUrl) => {
    console.log("registerTask", taskId);
    localStorage.setItem(taskId, wsUrl);

    if (wsRefs.current[taskId]) {
      wsRefs.current[taskId].close();
      delete wsRefs.current[taskId];
    }

    const ws = createWebSocketWithStatus(wsUrl, taskId, (message) => {
      // Update both status and full task data
      setTaskStatuses((prev) => ({
        ...prev,
        [taskId]: message.status || message,
      }));

      setTaskData((prev) => ({
        ...prev,
        [taskId]: message,
      }));
    });

    wsRefs.current[taskId] = ws;
  };

  // --- Deregister a task and stop listening ---
  const deregisterTask = (taskId) => {
    if (wsRefs.current[taskId]) {
      wsRefs.current[taskId].close();
      delete wsRefs.current[taskId];
    }

    setTaskStatuses((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });

    setTaskData((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });

    setTaskConnections((prev) => {
      const updated = { ...prev };
      delete updated[taskId];
      return updated;
    });
  };

  // --- Getters ---
  const getTaskStatus = (taskId) => taskStatuses[taskId];
  const getTaskData = (taskId) => taskData[taskId];
  const getConnectionStatus = (taskId) => taskConnections[taskId] ?? false;

  // --- Cleanup all WebSockets on unmount ---
  useEffect(() => {
    return () => {
      Object.values(wsRefs.current).forEach((ws) => ws.close());
      wsRefs.current = {};
    };
  }, []);

  return (
    <TaskStatusContext.Provider
      value={{
        registerTask,
        deregisterTask,
        getTaskStatus,
        getTaskData,
        getConnectionStatus,
        taskStatuses,
        taskData,
        taskConnections,
      }}
    >
      {children}
    </TaskStatusContext.Provider>
  );
};

// --- Hook to use the task status context ---
export const useTaskStatus = () => useContext(TaskStatusContext);
